/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Avatar,
  AvatarImage,
  useToast,
  Switch,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Spinner,
} from "../../components/ui";
import constant, { statuses, countries } from "../../constants";
import { getPaymentAccount } from "../../util/bankUtils";
import { isFavoriteUser, toggleFavoriteUser } from "../../util/favoriteUtils";
import { Header } from "../../components/modules";
import Context from "../../Context";

// Lucide & React Icons
import {
  User,
  Users as UsersIcon,
  Phone,
  Mail,
  MessageCircle,
  Calendar,
  ShieldCheck,
  ShieldAlert,
  CreditCard,
  Ticket,
  Store,
  MoreVertical,
  X,
  Check,
  Copy,
  LogIn,
  Lock,
  Plus,
  Coins,
  Wallet,
  FileText,
  Package,
  RefreshCw,
  MapPin,
  Building2,
  ExternalLink,
  Star,
  Trophy,
  AlertTriangle,
} from "lucide-react";

import th from "antd/es/date-picker/locale/th_TH";
import dayTh from "dayjs/locale/th";
import dayjs from "dayjs";
import { DatePicker } from "antd";

dayjs.locale(dayTh);

// ── Field Labels Mapping for Diff Tracker ─────────────────────────────────────
const FIELD_LABELS = {
  status: "สถานะบัญชี",
  firstName: "ชื่อ",
  lastName: "นามสกุล",
  firstNameEn: "ชื่อ (อังกฤษ)",
  lastNameEn: "นามสกุล (อังกฤษ)",
  phone: "เบอร์โทรศัพท์",
  phoneStatus: "สถานะเบอร์โทร",
  email: "อีเมล์",
  nationality: "สัญชาติ",
  dob: "วันเกิด",
  lineTitle: "ชื่อไลน์",
  lineId: "ไอดีไลน์",
  cartTime: "เวลาตะกร้า (นาที)",
  paymentMethod: "การชำระเงินหลัก",
  "bank.name": "ธนาคารไทย",
  "bank.accName": "ชื่อบัญชีไทย",
  "bank.accNo": "เลขบัญชีไทย",
  "paymentAccounts.TH_BANK.name": "ธนาคารไทย",
  "paymentAccounts.TH_BANK.accName": "ชื่อบัญชีไทย",
  "paymentAccounts.TH_BANK.accNo": "เลขบัญชีไทย",
  "laoBank.name": "ธนาคารลาว",
  "laoBank.accName": "ชื่อบัญชีลาว",
  "laoBank.accNo": "เลขบัญชีลาว",
  "paymentAccounts.LAO_BANK.name": "ธนาคารลาว",
  "paymentAccounts.LAO_BANK.accName": "ชื่อบัญชีลาว",
  "paymentAccounts.LAO_BANK.accNo": "เลขบัญชีลาว",
  alipayAcc: "บัญชี Alipay",
  alipayQrUrl: "QR Code Alipay",
  "paymentAccounts.ALIPAY.accNo": "บัญชี Alipay",
  "paymentAccounts.ALIPAY.qrUrl": "QR Code Alipay",
  "address.addrId": "ที่อยู่ตามบัตรปชช",
  "address.provinceId": "จังหวัดตามบัตรปชช",
  "address.zipcodeId": "รหัสไปรษณีย์ตามบัตรปชช",
  "address.addr": "ที่อยู่จัดส่ง",
  "address.province": "จังหวัดจัดส่ง",
  "address.zipcode": "รหัสไปรษณีย์จัดส่ง",
};

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split(".");
  let current = obj;
  for (const k of keys) {
    if (current === null || current === undefined) return undefined;
    current = current[k];
  }
  return current;
};

// ── Reusable Form Components ──────────────────────────────────────────────────

const FormField = ({ label, icon, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 w-full min-w-0 ${className}`}>
    {label && (
      <label className="flex items-center gap-1.5 font-semibold text-xs text-slate-600">
        {icon && <span className="text-slate-400">{icon}</span>}
        <span>{label}</span>
      </label>
    )}
    {children}
  </div>
);

const FormInput = ({ label, icon, isModified, className = "", ...props }) => (
  <FormField label={label} icon={icon} className={className}>
    <input
      className={`border w-full py-2 px-3 font-medium rounded-md text-sm text-slate-800 focus:outline-none transition-all disabled:bg-slate-100 disabled:text-slate-400 placeholder:text-slate-400 ${isModified
          ? "border-amber-500 bg-amber-50/40 ring-2 ring-amber-200 focus:border-amber-600"
          : "border-slate-200 bg-white focus:border-blue focus:ring-1 focus:ring-blue/20"
        }`}
      {...props}
    />
  </FormField>
);

const FormSelect = ({ label, icon, isModified, children, className = "", ...props }) => (
  <FormField label={label} icon={icon} className={className}>
    <select
      className={`border w-full py-2 px-3 font-medium rounded-md text-sm text-slate-800 focus:outline-none transition-all disabled:bg-slate-100 disabled:text-slate-400 cursor-pointer ${isModified
          ? "border-amber-500 bg-amber-50/40 ring-2 ring-amber-200 focus:border-amber-600"
          : "border-slate-200 bg-white focus:border-blue focus:ring-1 focus:ring-blue/20"
        }`}
      {...props}
    >
      {children}
    </select>
  </FormField>
);

const CardSection = ({ title, icon, action, children, className = "" }) => (
  <div className={`bg-white rounded-md shadow-xs border border-slate-200/80 overflow-hidden w-full ${className}`}>
    {title && (
      <div className="flex bg-slate-50/70 items-center justify-between gap-4 px-4 py-3 border-b border-slate-200/80">
        <div className="flex items-center gap-2">
          {icon && <span className="text-blue">{icon}</span>}
          <h2 className="font-bold text-sm text-slate-800 tracking-tight">{title}</h2>
        </div>
        {action}
      </div>
    )}
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

// ── Copyable Badge / Text ─────────────────────────────────────────────────────

const CopyableBadge = ({ text, label = "", icon, prefix = "" }) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!text || text === "-") return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast({
      className: "bg-slate-800 text-white text-xs font-semibold py-2 px-3 rounded-md",
      title: `คัดลอก ${label || text} เรียบร้อยแล้ว`,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  if (!text || text === "-") return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="คลิกเพื่อคัดลอก"
      className="inline-flex items-center gap-1.5 px-2 py-1 bg-white/90 hover:bg-white text-slate-700 text-xs font-medium rounded-md border border-slate-200/80 shadow-2xs hover:border-blue/40 transition-all cursor-pointer group"
    >
      {icon}
      <span>{prefix}{text}</span>
      {copied ? (
        <Check className="w-3 h-3 text-emerald-600" />
      ) : (
        <Copy className="w-3 h-3 text-slate-400 group-hover:text-blue transition-colors" />
      )}
    </button>
  );
};

// ── Edit Actions (in the side column, never over the form) ────────────────────

const UserEditActions = ({ userInfo, user, onSave, onDiscard, onRemoveItem, loading }) => {
  const modifiedKeys = Object.keys(userInfo || {});
  const modifiedCount = modifiedKeys.length;

  if (modifiedCount === 0) return null;

  return (
    <section aria-label="การแก้ไขข้อมูลลูกค้า" className="bg-white border border-slate-200 rounded-md p-4 flex flex-col gap-3">
      <p role="status" className="text-sm font-semibold text-slate-600">
        มีการแก้ไขข้อมูล {modifiedCount} รายการ
      </p>
      <button
        type="button"
        onClick={onSave}
        disabled={loading}
        className="w-full px-4 py-2.5 text-sm font-bold bg-blue hover:bg-blue/90 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
      >
        {loading && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
        {loading ? "กำลังบันทึก..." : "บันทึกการเปลี่ยนแปลง"}
      </button>
      <button
        type="button"
        onClick={onDiscard}
        disabled={loading}
        className="w-full px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        ยกเลิกการแก้ไข
      </button>

      <details className="border-t border-slate-100 pt-3">
        <summary className="text-xs font-semibold text-slate-500 cursor-pointer">
          ดูรายการที่แก้ไข
        </summary>
        <div className="flex flex-col gap-2 mt-3">
          {modifiedKeys.map((key) => {
            const label = FIELD_LABELS[key] || key;
            const newVal = userInfo[key];
            const rawOldVal = getNestedValue(user, key);
            const oldDisplay = rawOldVal !== undefined && rawOldVal !== null && rawOldVal !== "" ? String(rawOldVal) : "-";
            const newDisplay = newVal !== undefined && newVal !== null && newVal !== "" ? String(newVal) : "-";

            return (
              <div
                key={key}
                className="flex items-start justify-between gap-2 text-xs py-2 border-b border-slate-100 last:border-b-0 text-slate-700"
              >
                <div className="flex flex-col gap-1 min-w-0">
                  <span className="font-semibold text-slate-600">• {label}:</span>
                  <span className="break-words text-slate-400 line-through text-[11px]" title={oldDisplay}>
                    "{oldDisplay}"
                  </span>
                  <span className="sr-only">➔</span>
                  <span className="break-words font-semibold text-slate-800" title={newDisplay}>
                    "{newDisplay}"
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveItem(key)}
                  disabled={loading}
                  title={`ยกเลิกการแก้ไข ${label}`}
                  className="shrink-0 flex items-center gap-1 text-slate-500 hover:text-rose-600 hover:bg-rose-50 px-1.5 py-1 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  <span className="text-[10px]">ยกเลิก</span>
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            );
          })}
        </div>
      </details>
    </section>
  );
};

// ── Hero Profile Banner ───────────────────────────────────────────────────────

const HeroProfileBanner = ({
  user,
  userId,
  loading,
  onResetPassword,
  onLoginAsUser,
}) => {
  const { toast } = useToast();
  const [isFav, setIsFav] = useState(() => isFavoriteUser(userId));

  useEffect(() => {
    const handler = () => setIsFav(isFavoriteUser(userId));
    window.addEventListener("favorite_users_updated", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("favorite_users_updated", handler);
      window.removeEventListener("storage", handler);
    };
  }, [userId]);

  const handleToggleFavorite = () => {
    const nowFav = toggleFavoriteUser({ ...user, id: userId, userId });
    setIsFav(nowFav);
    toast({
      className: nowFav ? "bg-amber-600 text-white font-semibold" : "bg-slate-800 text-white font-semibold",
      title: nowFav ? "⭐ เพิ่มผู้ใช้ลงในรายการโปรดแล้ว" : "นำออกจากรายการโปรดเรียบร้อย",
    });
  };

  const isVerified = user?.verifiedId === statuses.VERIFIED || user?.verifiedId === "verified";
  const isPendingVerify = user?.verifiedId === "pending";

  const getInitials = () => {
    const f = user?.firstName?.[0] || "";
    const l = user?.lastName?.[0] || "";
    return (f + l).toUpperCase() || <User className="w-6 h-6 text-slate-400" />;
  };

  return (
    <div className="bg-white rounded-md p-4 sm:p-6 shadow-xs border border-slate-200/80">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        {/* Left: Avatar + Identity Info */}
        <div className="flex items-start sm:items-center gap-4 flex-wrap sm:flex-nowrap">
          <Avatar className="w-20 h-20 sm:w-24 sm:h-24 rounded-md border-2 border-slate-100 shadow-xs bg-slate-50 flex items-center justify-center text-lg font-bold text-slate-600 shrink-0">
            {user?.profileImage ? (
              <AvatarImage src={user.profileImage} alt={user.firstName} className="rounded-md object-cover" />
            ) : (
              <span>{getInitials()}</span>
            )}
          </Avatar>

          <div className="flex flex-col gap-1.5 min-w-0">
            {/* Name, Role & Favorite Star */}
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight leading-tight">
                {user?.firstName || "-"} {user?.lastName || ""}
              </h1>
              {user?.firstNameEn && (
                <span className="text-xs font-semibold text-slate-400">
                  ({user.firstNameEn} {user.lastNameEn})
                </span>
              )}
              {user?.role && user?.role !== "user" && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-blue/10 text-blue border border-blue/20 rounded-md">
                  {user.role}
                </span>
              )}

              {/* Favorite Star Toggle */}
              <button
                type="button"
                onClick={handleToggleFavorite}
                title={isFav ? "คลิกเพื่อนำออกจากรายการโปรด" : "คลิกเพื่อเพิ่มเป็นลูกค้าคนโปรด (Favorite)"}
                className={`p-1.5 rounded-md border transition-all cursor-pointer flex items-center gap-1 text-xs font-bold ${isFav
                    ? "bg-amber-50 text-amber-600 border-amber-300 hover:bg-amber-100 shadow-2xs"
                    : "bg-slate-50 text-slate-400 border-slate-200 hover:text-amber-500 hover:bg-amber-50/50"
                  }`}
              >
                <Star className={`w-3.5 h-3.5 ${isFav ? "fill-amber-400 text-amber-500" : ""}`} />
                <span>{isFav ? "รายการโปรด" : "ติดดาว"}</span>
              </button>
            </div>

            {/* Badges: UserID, Phone, Line */}
            <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600">
              <CopyableBadge text={userId} label="รหัสผู้ใช้" prefix="ID: " icon={<User className="w-3 h-3 text-slate-400" />} />
              <CopyableBadge text={user?.phone} label="เบอร์โทร" icon={<Phone className="w-3 h-3 text-slate-400" />} />
              {user?.lineTitle && (
                <CopyableBadge text={user.lineTitle} label="Line Title" icon={<MessageCircle className="w-3 h-3 text-emerald-500" />} />
              )}
            </div>

            {/* Verification Status Pills */}
            <div className="flex items-center gap-2 flex-wrap pt-1">
              {isVerified ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  บัตรประชาชน: ยืนยันแล้ว
                </span>
              ) : isPendingVerify ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
                  บัตรประชาชน: รอยืนยัน
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
                  บัตรประชาชน: ยังไม่ยืนยัน
                </span>
              )}

              {user?.activated ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-blue/10 text-blue border border-blue/20">
                  <Check className="w-3 h-3" />
                  OTP: ยืนยันแล้ว
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-400">
                  OTP: ยังไม่ยืนยัน
                </span>
              )}

              {user?.status && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold border ${user.status === statuses.ACTIVE
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-rose-50 text-rose-700 border-rose-200"
                  }`}>
                  สถานะ: {user.status === statuses.ACTIVE ? "เปิดใช้งาน" : "ระงับการใช้งาน"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: Quick Stats & Actions */}
        <div className="flex flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-end gap-3 shrink-0">
          {/* Points & Referral Earnings Snapshot */}
          <div className="flex items-center gap-3 bg-slate-50/80 border border-slate-200/80 p-2.5 rounded-md text-xs">
            <div className="flex items-center gap-2 pr-3 border-r border-slate-200">
              <Coins className="w-4 h-4 text-emerald-600" />
              <div>
                <p className="text-[10px] text-slate-500 font-medium leading-none">คะแนนสะสม</p>
                <p className="font-mono font-bold text-emerald-700 text-sm mt-0.5">
                  {Number(user?.buyPoint || 0).toLocaleString()} <span className="text-[10px] font-sans font-normal text-slate-500">แต้ม</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-blue" />
              <div>
                <p className="text-[10px] text-slate-500 font-medium leading-none">รายได้ชวนเพื่อน</p>
                <p className="font-mono font-bold text-blue text-sm mt-0.5">
                  {Number(user?.buyCash || 0).toLocaleString()} <span className="text-[10px] font-sans font-normal text-slate-500">บาท</span>
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Login as User */}
            <button
              type="button"
              disabled={loading}
              onClick={onLoginAsUser}
              className="h-9 px-3 bg-blue hover:bg-blue/90 text-white text-xs font-bold rounded-md shadow-xs flex items-center gap-1.5 transition-all disabled:opacity-50 cursor-pointer"
            >
              <LogIn className="w-3.5 h-3.5" />
              <span>เข้าสู่ระบบแทนลูกค้า</span>
            </button>

            {/* Reset Password */}
            <button
              type="button"
              disabled={loading}
              onClick={onResetPassword}
              className="h-9 px-3 bg-slate-700 hover:bg-slate-800 text-white text-xs font-bold rounded-md shadow-xs flex items-center gap-1.5 transition-all disabled:opacity-50 cursor-pointer"
            >
              <Lock className="w-3.5 h-3.5" />
              <span>รีเซ็ตรหัสผ่าน</span>
            </button>

            {/* Create Order Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                title="สร้างคำสั่งซื้อ"
                className="h-9 w-9 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-md shadow-xs flex items-center justify-center transition-all cursor-pointer shrink-0"
              >
                <MoreVertical className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-52" align="end">
                <DropdownMenuLabel className="text-xs font-bold text-slate-700">
                  สร้างคำสั่งซื้อให้ลูกค้า
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => window.open(`/orders/create/${userId}`, "_blank")}
                  className="cursor-pointer flex items-center justify-between text-xs py-2"
                >
                  <span className="font-semibold">คำสั่งซื้อสลาก</span>
                  <CreditCard className="w-4 h-4 text-blue" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.open(`/coupon/order/create/${userId}`, "_blank")}
                  className="cursor-pointer flex items-center justify-between text-xs py-2"
                >
                  <span className="font-semibold">คำสั่งซื้อคูปอง</span>
                  <Ticket className="w-4 h-4 text-amber-500" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.open(`/product/order/create/${userId}`, "_blank")}
                  className="cursor-pointer flex items-center justify-between text-xs py-2"
                >
                  <span className="font-semibold">คำสั่งซื้อร้านค้า</span>
                  <Store className="w-4 h-4 text-emerald-600" />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Tab 1: General Info & Identity ────────────────────────────────────────────

const GeneralInfoTab = ({
  user,
  userInfo,
  loading,
  handleInputChange,
  handleSwitchUpdate,
  profileState,
}) => {
  return (
    <div className="space-y-4">
      {/* 1. Account Controls & Quick Switches */}
      <CardSection title="การตั้งค่าความปลอดภัยและบัญชี" icon={<ShieldCheck className="w-4 h-4" />}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between p-3 bg-slate-50/70 border border-slate-200/80 rounded-md">
            <div>
              <p className="text-xs font-bold text-slate-800">ยืนยันบัตรประชาชน</p>
              <p className="text-[11px] text-slate-500">สถานะการตรวจบัตร ปชช.</p>
            </div>
            <Switch
              checked={user?.verifiedId === statuses.VERIFIED}
              disabled={user?.verifiedId === statuses.VERIFIED}
              onCheckedChange={async (e) => {
                handleSwitchUpdate("verifiedId", e ? statuses.VERIFIED : false);
              }}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50/70 border border-slate-200/80 rounded-md">
            <div>
              <p className="text-xs font-bold text-slate-800">ยืนยันเบอร์โทร (OTP)</p>
              <p className="text-[11px] text-slate-500">การยืนยันรหัส OTP ทางมือถือ</p>
            </div>
            <Switch
              checked={user?.activated}
              onCheckedChange={async (e) => {
                handleSwitchUpdate("activated", e);
              }}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50/70 border border-slate-200/80 rounded-md">
            <div>
              <p className="text-xs font-bold text-slate-800">เปิดใช้งานตะกร้า</p>
              <p className="text-[11px] text-slate-500">อนุญาตให้เลือกสลากใส่ตะกร้า</p>
            </div>
            <Switch
              checked={!user?.disabled?.cart}
              onCheckedChange={(e) => handleSwitchUpdate("disabled.cart", !e)}
            />
          </div>
        </div>

        {/* OTP Diagnostics & Cart Duration */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-4 text-xs">
          <FormInput label="รหัส OTP ล่าสุด" disabled value={user?.otp?.code || "-"} />
          <FormInput label="รหัสอ้างอิง OTP (Ref)" disabled value={user?.otp?.ref || "-"} />
          <FormInput
            label="เวลาหมดอายุ OTP"
            disabled
            value={
              user?.otp?.expireAt
                ? new Date(user?.otp?.expireAt).toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                }) + " น."
                : "-"
            }
          />
          <FormInput
            label="เวลาตะกร้า (นาที)"
            type="number"
            disabled={loading}
            isModified={userInfo["cartTime"] !== undefined}
            value={userInfo["cartTime"] !== undefined ? userInfo["cartTime"] : (user?.cartTime || "")}
            onChange={(e) => handleInputChange("cartTime", e.target.value)}
            placeholder="เช่น 15"
          />
        </div>
      </CardSection>

      {/* 2. Personal Information Fields */}
      <CardSection title="ข้อมูลส่วนบุคคลและข้อมูลติดต่อ" icon={<User className="w-4 h-4" />}>
        {/* Banner แสดงสถานะการลบและเซนเซอร์ข้อมูล (Read-only) */}
        {user?.status === "censored" && (
          <div className="mb-3 p-3 bg-slate-100 border border-slate-200 rounded-md flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2 text-slate-700 font-bold">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>บัญชีนี้ได้รับการอนุมัติลบและเซนเซอร์ข้อมูลส่วนบุคคล (PDPA Sanitized) เรียบร้อยแล้ว</span>
            </div>
            {user?.anonymizedAt && (
              <span className="text-[11px] text-slate-500 font-medium shrink-0">
                เซนเซอร์เมื่อ: {new Date(user.anonymizedAt).toLocaleDateString("th-TH")}
              </span>
            )}
          </div>
        )}

        {user?.status === statuses.REMOVED && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-md flex items-center gap-2 text-xs text-amber-950">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <div>
              <span className="font-bold text-amber-900">บัญชีนี้อยู่ในสถานะถูกลบ (Removed)</span>
              <p className="text-[11px] text-amber-700">รอ Dev หรือ Super Admin ทำการตรวจสอบและอนุมัติลบ/เซนเซอร์ข้อมูลผ่านระบบ</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <FormSelect
            label="สถานะการใช้งานบัญชี"
            disabled={loading}
            isModified={userInfo["status"] !== undefined}
            value={userInfo["status"] !== undefined ? userInfo["status"] : (user?.status || "")}
            onChange={(e) => handleInputChange("status", e.target.value)}
          >
            <option value={statuses.ACTIVE}>1. เปิดใช้งาน (Active)</option>
            <option value={statuses.INACTIVE}>2. ระงับการใช้งาน (Inactive)</option>
            <option value={statuses.REMOVED}>3. ลบผู้ใช้งาน (Removed)</option>
          </FormSelect>

          <FormField label="วันเกิด (ค.ศ.)" icon={<Calendar className="w-3.5 h-3.5" />}>
            <DatePicker
              className={`border w-full py-2 px-3 font-medium rounded-md text-sm text-slate-800 transition-all ${userInfo["dob"] !== undefined
                  ? "border-amber-500 bg-amber-50/40 ring-2 ring-amber-200"
                  : "border-slate-200 bg-white focus:border-blue focus:ring-1 focus:ring-blue/20"
                }`}
              locale={th}
              format="DD-MM-YYYY"
              placeholder="วัน-เดือน-ปี ค.ศ."
              value={
                userInfo["dob"] !== undefined
                  ? (userInfo["dob"] ? dayjs(userInfo["dob"]) : null)
                  : user?.dob
                    ? dayjs(user.dob).isValid()
                      ? dayjs(user.dob)
                      : dayjs(user.dob, "YYYY-MM-DD").isValid()
                        ? dayjs(user.dob, "YYYY-MM-DD")
                        : null
                    : null
              }
              disabled={loading}
              onChange={(date) => {
                handleInputChange("dob", date ? date.format("YYYY-MM-DD") : "");
              }}
            />
          </FormField>

          <FormInput
            label="ชื่อ (ภาษาไทย)"
            disabled={loading}
            isModified={userInfo["firstName"] !== undefined}
            value={userInfo["firstName"] !== undefined ? userInfo["firstName"] : (user?.firstName || "")}
            onChange={(e) => handleInputChange("firstName", e.target.value)}
            placeholder="ชื่อจริง"
          />

          <FormInput
            label="นามสกุล (ภาษาไทย)"
            disabled={loading}
            isModified={userInfo["lastName"] !== undefined}
            value={userInfo["lastName"] !== undefined ? userInfo["lastName"] : (user?.lastName || "")}
            onChange={(e) => handleInputChange("lastName", e.target.value)}
            placeholder="นามสกุล"
          />

          <FormInput
            label="ชื่อ (English)"
            disabled={loading}
            isModified={userInfo["firstNameEn"] !== undefined}
            value={userInfo["firstNameEn"] !== undefined ? userInfo["firstNameEn"] : (user?.firstNameEn || "")}
            onChange={(e) => handleInputChange("firstNameEn", e.target.value)}
            placeholder="First name in English"
          />

          <FormInput
            label="นามสกุล (English)"
            disabled={loading}
            isModified={userInfo["lastNameEn"] !== undefined}
            value={userInfo["lastNameEn"] !== undefined ? userInfo["lastNameEn"] : (user?.lastNameEn || "")}
            onChange={(e) => handleInputChange("lastNameEn", e.target.value)}
            placeholder="Last name in English"
          />

          <FormInput
            label="เบอร์โทรศัพท์"
            icon={<Phone className="w-3.5 h-3.5" />}
            disabled={loading}
            isModified={userInfo["phone"] !== undefined}
            value={userInfo["phone"] !== undefined ? userInfo["phone"] : (user?.phone || "")}
            onChange={(e) => handleInputChange("phone", e.target.value.replaceAll(" ", "").replaceAll("-", ""))}
            placeholder="เบอร์โทรศัพท์ 10 หลัก"
          />

          <FormSelect
            label="สถานะเบอร์โทรศัพท์"
            disabled={loading}
            isModified={userInfo["phoneStatus"] !== undefined}
            value={userInfo["phoneStatus"] !== undefined ? userInfo["phoneStatus"] : (user?.phoneStatus || "")}
            onChange={(e) => handleInputChange("phoneStatus", e.target.value)}
          >
            <option value="">- เลือกสถานะการโทร -</option>
            <option value="check">รับสายและปิดการขาย</option>
            <option value="recheck">ไม่รับสาย / เบอร์ผิด</option>
            <option value="remove">เบอร์ระงับ / ปิดเครื่อง</option>
          </FormSelect>

          <FormInput
            label="อีเมล์"
            icon={<Mail className="w-3.5 h-3.5" />}
            disabled={loading}
            isModified={userInfo["email"] !== undefined}
            value={userInfo["email"] !== undefined ? userInfo["email"] : (user?.email || "")}
            onChange={(e) => handleInputChange("email", e.target.value.replaceAll(" ", ""))}
            placeholder="อีเมล์ลูกค้า"
          />

          <FormSelect
            label="สัญชาติ"
            disabled={loading}
            isModified={userInfo["nationality"] !== undefined}
            value={userInfo["nationality"] !== undefined ? userInfo["nationality"] : (user?.nationality || "")}
            onChange={(e) => handleInputChange("nationality", e.target.value)}
          >
            <option value="">- เลือกสัญชาติ -</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>{c.nationality}</option>
            ))}
          </FormSelect>

          <FormInput
            label="ชื่อไลน์ (Line Display)"
            icon={<MessageCircle className="w-3.5 h-3.5 text-emerald-500" />}
            disabled={loading}
            isModified={userInfo["lineTitle"] !== undefined}
            value={userInfo["lineTitle"] !== undefined ? userInfo["lineTitle"] : (user?.lineTitle || "")}
            onChange={(e) => handleInputChange("lineTitle", e.target.value)}
            placeholder="ชื่อไลน์ลูกค้า"
          />

          <FormInput
            label="ไอดีไลน์ (Line ID)"
            disabled={loading}
            isModified={userInfo["lineId"] !== undefined}
            value={userInfo["lineId"] !== undefined ? userInfo["lineId"] : (user?.lineId || "")}
            onChange={(e) => handleInputChange("lineId", e.target.value)}
            placeholder="Line ID"
          />
        </div>
      </CardSection>

      {/* 3. ID Card Registered Address */}
      <CardSection title="ที่อยู่ตามบัตรประชาชน" icon={<Building2 className="w-4 h-4" />}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <div className="sm:col-span-3">
            <FormInput
              label="ที่อยู่ตามบัตรประชาชน"
              disabled={loading}
              isModified={userInfo["address.addrId"] !== undefined}
              value={userInfo["address.addrId"] !== undefined ? userInfo["address.addrId"] : (user?.address?.addrId || "")}
              onChange={(e) => handleInputChange("address.addrId", e.target.value)}
              placeholder="บ้านเลขที่, หมู่, ซอย, ถนน, ตำบล/แขวง, อำเภอ/เขต"
            />
          </div>

          <FormSelect
            label="จังหวัด (ตามบัตรประชาชน)"
            disabled={loading}
            isModified={userInfo["address.provinceId"] !== undefined}
            value={userInfo["address.provinceId"] !== undefined ? userInfo["address.provinceId"] : (user?.address?.provinceId || "")}
            onChange={(e) => handleInputChange("address.provinceId", e.target.value)}
          >
            <option value="">- เลือกจังหวัด -</option>
            {constant.provinces.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </FormSelect>

          <FormInput
            label="รหัสไปรษณีย์"
            disabled={loading}
            isModified={userInfo["address.zipcodeId"] !== undefined}
            value={userInfo["address.zipcodeId"] !== undefined ? userInfo["address.zipcodeId"] : (user?.address?.zipcodeId || "")}
            onChange={(e) => handleInputChange("address.zipcodeId", e.target.value.replaceAll(" ", ""))}
            placeholder="รหัสไปรษณีย์ 5 หลัก"
          />
        </div>
      </CardSection>
    </div>
  );
};

// ── Tab 2: Payments & Shipping ────────────────────────────────────────────────

const PaymentsShippingTab = ({
  user,
  userInfo,
  loading,
  handleInputChange,
}) => {
  const currentMethod = userInfo["paymentMethod"] !== undefined ? userInfo["paymentMethod"] : (user?.paymentMethod || "TH_BANK");

  const thAcc = getPaymentAccount(user, "TH_BANK");
  const laoAcc = getPaymentAccount(user, "LAO_BANK");
  const alipayAcc = getPaymentAccount(user, "ALIPAY");

  return (
    <div className="space-y-4">
      {/* 1. Payment Method & Bank Accounts */}
      <CardSection title="การชำระเงินและบัญชีรับเงินรางวัล" icon={<CreditCard className="w-4 h-4" />}>
        <div className="space-y-4">
          <FormSelect
            label="ช่องทางการชำระเงินและรับเงินรางวัลหลัก (Primary Payment Method)"
            disabled={loading}
            isModified={userInfo["paymentMethod"] !== undefined}
            value={currentMethod}
            onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
          >
            <option value="TH_BANK">1. บัญชีธนาคารไทย (THB) 🇹🇭</option>
            <option value="LAO_BANK">2. บัญชีธนาคาร สปป.ลาว (LAK) 🇱🇦</option>
            <option value="ALIPAY">3. ชำระด้วย Alipay (CNY) 🇨🇳</option>
          </FormSelect>

          {/* Thai Bank Section */}
          <div className={`p-4 rounded-md border transition-all ${currentMethod === "TH_BANK" ? "bg-blue/5 border-blue/30" : "bg-slate-50/50 border-slate-200"
            }`}>
            <p className="font-bold text-xs text-blue flex items-center gap-1.5 mb-3">
              <span>🇹🇭</span>
              <span>บัญชีธนาคารไทย (THB)</span>
              {currentMethod === "TH_BANK" && (
                <span className="text-[10px] font-semibold bg-blue text-white px-1.5 py-0.2 rounded-md">
                  ช่องทางหลัก
                </span>
              )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FormSelect
                label="ธนาคารไทย"
                disabled={loading}
                isModified={userInfo["paymentAccounts.TH_BANK.name"] !== undefined || userInfo["bank.name"] !== undefined}
                value={userInfo["paymentAccounts.TH_BANK.name"] !== undefined ? userInfo["paymentAccounts.TH_BANK.name"] : (userInfo["bank.name"] !== undefined ? userInfo["bank.name"] : thAcc.name)}
                onChange={(e) => {
                  handleInputChange("paymentAccounts.TH_BANK.name", e.target.value);
                  handleInputChange("paymentAccounts.TH_BANK.currency", "THB");
                }}
              >
                <option value="">- เลือกธนาคารไทย -</option>
                {constant.bankList.filter((b) => b.currency === "THB").map((bank) => (
                  <option key={bank.bankName} value={bank.bankName}>{bank.bankName}</option>
                ))}
              </FormSelect>

              <FormInput
                label="ชื่อบัญชี"
                disabled={loading}
                isModified={userInfo["paymentAccounts.TH_BANK.accName"] !== undefined || userInfo["bank.accName"] !== undefined}
                value={userInfo["paymentAccounts.TH_BANK.accName"] !== undefined ? userInfo["paymentAccounts.TH_BANK.accName"] : (userInfo["bank.accName"] !== undefined ? userInfo["bank.accName"] : thAcc.accName)}
                onChange={(e) => handleInputChange("paymentAccounts.TH_BANK.accName", e.target.value)}
                placeholder="ชื่อ-นามสกุลเจ้าของบัญชี"
              />

              <FormInput
                label="เลขที่บัญชี"
                inputMode="numeric"
                disabled={loading}
                isModified={userInfo["paymentAccounts.TH_BANK.accNo"] !== undefined || userInfo["bank.accNo"] !== undefined}
                value={userInfo["paymentAccounts.TH_BANK.accNo"] !== undefined ? userInfo["paymentAccounts.TH_BANK.accNo"] : (userInfo["bank.accNo"] !== undefined ? userInfo["bank.accNo"] : thAcc.accNo)}
                onChange={(e) => handleInputChange("paymentAccounts.TH_BANK.accNo", e.target.value.replace(/\D/g, ""))}
                placeholder="เลขบัญชีธนาคาร (เฉพาะตัวเลข)"
              />
            </div>
          </div>

          {/* Lao Bank Section */}
          <div className={`p-4 rounded-md border transition-all ${currentMethod === "LAO_BANK" ? "bg-teal-50 border-teal-200" : "bg-slate-50/50 border-slate-200"
            }`}>
            <p className="font-bold text-xs text-teal-700 flex items-center gap-1.5 mb-3">
              <span>🇱🇦</span>
              <span>บัญชีธนาคาร สปป.ลาว (LAK)</span>
              {currentMethod === "LAO_BANK" && (
                <span className="text-[10px] font-semibold bg-teal-600 text-white px-1.5 py-0.2 rounded-md">
                  ช่องทางหลัก
                </span>
              )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FormSelect
                label="ธนาคารลาว"
                disabled={loading}
                isModified={userInfo["paymentAccounts.LAO_BANK.name"] !== undefined || userInfo["laoBank.name"] !== undefined}
                value={userInfo["paymentAccounts.LAO_BANK.name"] !== undefined ? userInfo["paymentAccounts.LAO_BANK.name"] : (userInfo["laoBank.name"] !== undefined ? userInfo["laoBank.name"] : laoAcc.name)}
                onChange={(e) => {
                  handleInputChange("paymentAccounts.LAO_BANK.name", e.target.value);
                  handleInputChange("paymentAccounts.LAO_BANK.currency", "LAK");
                }}
              >
                <option value="">- เลือกธนาคารลาว -</option>
                {constant.bankList.filter((b) => b.currency === "LAK").map((bank) => (
                  <option key={bank.bankName} value={bank.bankName}>{bank.bankName}</option>
                ))}
              </FormSelect>

              <FormInput
                label="ชื่อบัญชีลาว"
                disabled={loading}
                isModified={userInfo["paymentAccounts.LAO_BANK.accName"] !== undefined || userInfo["laoBank.accName"] !== undefined}
                value={userInfo["paymentAccounts.LAO_BANK.accName"] !== undefined ? userInfo["paymentAccounts.LAO_BANK.accName"] : (userInfo["laoBank.accName"] !== undefined ? userInfo["laoBank.accName"] : laoAcc.accName)}
                onChange={(e) => handleInputChange("paymentAccounts.LAO_BANK.accName", e.target.value)}
                placeholder="ชื่อบัญชีลาว"
              />

              <FormInput
                label="เลขที่บัญชีลาว"
                inputMode="numeric"
                disabled={loading}
                isModified={userInfo["paymentAccounts.LAO_BANK.accNo"] !== undefined || userInfo["laoBank.accNo"] !== undefined}
                value={userInfo["paymentAccounts.LAO_BANK.accNo"] !== undefined ? userInfo["paymentAccounts.LAO_BANK.accNo"] : (userInfo["laoBank.accNo"] !== undefined ? userInfo["laoBank.accNo"] : laoAcc.accNo)}
                onChange={(e) => handleInputChange("paymentAccounts.LAO_BANK.accNo", e.target.value.replace(/\D/g, ""))}
                placeholder="เลขบัญชีลาว"
              />
            </div>
          </div>

          {/* Alipay Section */}
          <div className={`p-4 rounded-md border transition-all ${currentMethod === "ALIPAY" ? "bg-sky-50 border-sky-200" : "bg-slate-50/50 border-slate-200"
            }`}>
            <p className="font-bold text-xs text-sky-700 flex items-center gap-1.5 mb-3">
              <span>🇨🇳</span>
              <span>บัญชี Alipay (CNY)</span>
              {currentMethod === "ALIPAY" && (
                <span className="text-[10px] font-semibold bg-sky-600 text-white px-1.5 py-0.2 rounded-md">
                  ช่องทางหลัก
                </span>
              )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormInput
                label="Alipay ID / ชื่อบัญชี Alipay"
                disabled={loading}
                isModified={userInfo["paymentAccounts.ALIPAY.accNo"] !== undefined || userInfo["alipayAcc"] !== undefined}
                value={userInfo["paymentAccounts.ALIPAY.accNo"] !== undefined ? userInfo["paymentAccounts.ALIPAY.accNo"] : (userInfo["alipayAcc"] !== undefined ? userInfo["alipayAcc"] : alipayAcc.accNo)}
                onChange={(e) => {
                  handleInputChange("paymentAccounts.ALIPAY.accNo", e.target.value);
                  handleInputChange("paymentAccounts.ALIPAY.accName", e.target.value);
                  handleInputChange("paymentAccounts.ALIPAY.name", "Alipay");
                  handleInputChange("paymentAccounts.ALIPAY.currency", "CNY");
                }}
                placeholder="เช่น 13800138000 หรือ user@email.com"
              />

              <FormInput
                label="URL รูป QR Code Alipay"
                disabled={loading}
                isModified={userInfo["paymentAccounts.ALIPAY.qrUrl"] !== undefined || userInfo["alipayQrUrl"] !== undefined}
                value={userInfo["paymentAccounts.ALIPAY.qrUrl"] !== undefined ? userInfo["paymentAccounts.ALIPAY.qrUrl"] : (userInfo["alipayQrUrl"] !== undefined ? userInfo["alipayQrUrl"] : alipayAcc.qrUrl)}
                onChange={(e) => handleInputChange("paymentAccounts.ALIPAY.qrUrl", e.target.value)}
                placeholder="https://.../qrcode.jpg"
              />
            </div>
          </div>
        </div>
      </CardSection>

      {/* 2. Shipping Address */}
      <CardSection title="ที่อยู่สำหรับจัดส่งทางไปรษณีย์" icon={<MapPin className="w-4 h-4" />}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <div className="sm:col-span-3">
            <FormInput
              label="ที่อยู่สำหรับจัดส่ง"
              disabled={loading}
              isModified={userInfo["address.addr"] !== undefined}
              value={userInfo["address.addr"] !== undefined ? userInfo["address.addr"] : (user?.address?.addr || "")}
              onChange={(e) => handleInputChange("address.addr", e.target.value)}
              placeholder="บ้านเลขที่, อาคาร, หมู่บ้าน, ซอย, ถนน, ตำบล/แขวง, อำเภอ/เขต"
            />
          </div>

          <FormSelect
            label="จังหวัดจัดส่ง"
            disabled={loading}
            isModified={userInfo["address.province"] !== undefined}
            value={userInfo["address.province"] !== undefined ? userInfo["address.province"] : (user?.address?.province || "")}
            onChange={(e) => handleInputChange("address.province", e.target.value)}
          >
            <option value="">- เลือกจังหวัด -</option>
            {constant.provinces.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </FormSelect>

          <FormInput
            label="รหัสไปรษณีย์จัดส่ง"
            disabled={loading}
            isModified={userInfo["address.zipcode"] !== undefined}
            value={userInfo["address.zipcode"] !== undefined ? userInfo["address.zipcode"] : (user?.address?.zipcode || "")}
            onChange={(e) => handleInputChange("address.zipcode", e.target.value.replaceAll(" ", ""))}
            placeholder="รหัสไปรษณีย์ 5 หลัก"
          />
        </div>
      </CardSection>
    </div>
  );
};

// ── Tab 3: Points & Wallet Transactions ───────────────────────────────────────

const PointsTransactionsTab = ({ user, userId, onRefreshUser }) => {
  const { callApi, profileState } = Context();
  const { toast } = useToast();
  const [transactions, setTransactions] = useState(null);
  const [loadingTx, setLoadingTx] = useState(false);
  const [pointAmount, setPointAmount] = useState("");
  const [pointNote, setPointNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const t = {
    deposit: "ฝากเงิน",
    withdraw: "ถอนเงิน/หักรายได้",
    addPoint: "เพิ่มคะแนน",
    deductPoint: "ลดคะแนน/หักคะแนน",
  };

  const fetchTransactions = async () => {
    if (!userId) return;
    setLoadingTx(true);
    try {
      const result = await callApi("transactions-getAll", {
        queries: [["userId", userId]],
        orderBy: ["createdAt", "desc"],
        limit: 25,
      });
      setTransactions(result || []);
    } catch (err) {
      console.warn("Failed to get transactions:", err);
      setTransactions([]);
    } finally {
      setLoadingTx(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [userId]);

  const handleAdjustPoints = async (isAdd) => {
    const amountNum = parseInt(pointAmount, 10);
    if (!pointAmount || isNaN(amountNum) || amountNum <= 0) {
      toast({
        className: "bg-rose-600 text-white font-semibold",
        title: "กรุณาระบุจำนวนคะแนนที่ถูกต้อง (มากกว่า 0)",
      });
      return;
    }

    const actionText = isAdd ? "เพิ่มคะแนน" : "ลดคะแนน";
    if (!window.confirm(`ต้องการ ${actionText} จำนวน ${amountNum.toLocaleString()} คะแนน ใช่หรือไม่?`)) return;

    setActionLoading(true);
    try {
      const apiName = isAdd ? "transactions-addPoint" : "transactions-removePoint";
      const adminName = profileState?.firstName
        ? `${profileState.firstName} ${profileState.lastName || ""}`.trim()
        : profileState?.userId || "Admin";

      const res = await callApi(apiName, {
        userId: userId,
        amount: amountNum,
        note: {
          by: adminName,
          note: pointNote || `${actionText}โดยแอดมิน`,
        },
      });

      if (res && res.status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold",
          title: `${actionText}สำเร็จเรียบร้อย`,
        });
        setPointAmount("");
        setPointNote("");
        fetchTransactions();
        if (onRefreshUser) onRefreshUser();
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold",
          title: res?.toastMessage || `เกิดข้อผิดพลาดในการ${actionText}`,
        });
      }
    } catch (err) {
      toast({
        className: "bg-rose-600 text-white font-semibold",
        title: `เกิดข้อผิดพลาดในการ${actionText}`,
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* 1. Point Adjust Form */}
      <CardSection title="ปรับปรุงคะแนนสะสมผู้ใช้" icon={<Coins className="w-4 h-4" />}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 items-end">
          <FormInput
            label="จำนวนคะแนนที่ต้องการปรับ"
            type="number"
            disabled={actionLoading}
            value={pointAmount}
            onChange={(e) => setPointAmount(e.target.value)}
            placeholder="เช่น 100"
          />

          <FormInput
            label="เหตุผล / หมายเหตุ"
            disabled={actionLoading}
            value={pointNote}
            onChange={(e) => setPointNote(e.target.value)}
            placeholder="เช่น ชดเชยระบบ, กิจกรรมพิเศษ"
          />

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={actionLoading}
              onClick={() => handleAdjustPoints(true)}
              className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-md shadow-xs transition-all disabled:opacity-50 cursor-pointer flex items-center justify-center gap-1.5"
            >
              {actionLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              <span>เพิ่มคะแนน</span>
            </button>

            <button
              type="button"
              disabled={actionLoading}
              onClick={() => handleAdjustPoints(false)}
              className="flex-1 h-10 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-md shadow-xs transition-all disabled:opacity-50 cursor-pointer flex items-center justify-center gap-1.5"
            >
              {actionLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
              <span>ลดคะแนน</span>
            </button>
          </div>
        </div>
      </CardSection>

      {/* 2. Transaction History Feed */}
      <CardSection title="ประวัติการเคลื่อนไหวคะแนนและกระเป๋าเงิน (ล่าสุด 25 รายการ)" icon={<Wallet className="w-4 h-4" />}>
        {loadingTx ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-slate-400">
            <Spinner className="w-8 h-8" />
            <p className="text-xs font-semibold">กำลังโหลดประวัติธุรกรรม...</p>
          </div>
        ) : transactions && transactions.length > 0 ? (
          <div className="space-y-2.5">
            {transactions.map((tx, idx) => {
              const isPoint = tx.type === "addPoint" || tx.type === "deductPoint";
              const isAdd = tx.type === "addPoint" || tx.type === "deposit";
              const amountColor = isAdd ? "text-emerald-600" : "text-rose-600";
              const amountSign = tx.amount >= 0 ? `+${tx.amount}` : `${tx.amount}`;
              const typeLabel = t[tx.type] || tx.type;
              const unit = isPoint ? "คะแนน" : "บาท";
              const noteObj = tx.notes?.[0] || {};
              const noteBy = noteObj.by || tx.updatedBy || "ระบบ";
              const noteText = noteObj.note || tx.note || "";

              return (
                <div
                  key={tx.id || idx}
                  className="p-3 bg-white border border-slate-200/80 rounded-md shadow-2xs hover:border-slate-300 transition-all"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-xs text-slate-900">{typeLabel}</span>
                        <span className="text-[11px] text-slate-500 font-medium">โดย {noteBy}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                        {new Date(tx.createdAt).toLocaleDateString("th-TH", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })} น.
                      </p>
                    </div>

                    <span className={`font-mono font-extrabold text-sm ${amountColor}`}>
                      {amountSign} {unit}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-4 mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500 flex-wrap">
                    <div className="flex items-center gap-3">
                      <span>ก่อนปรับ: <strong className="font-mono text-slate-700">{tx.balanceBefore}</strong> {unit}</span>
                      <span>หลังปรับ: <strong className="font-mono text-slate-700">{tx.balanceAfter}</strong> {unit}</span>
                    </div>

                    {noteText && (
                      <span className="text-[11px] text-slate-500 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-200">
                        หมายเหตุ: {noteText}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-slate-400">
            <Coins className="w-8 h-8 text-slate-300" />
            <p className="text-xs font-semibold text-slate-600">ไม่พบประวัติธุรกรรม</p>
          </div>
        )}
      </CardSection>
    </div>
  );
};

// ── Tab 4: User Prizes History (Lazy Loaded) ─────────────────────────────────

const PRIZE_TRANSLATIONS = {
  first: { label: "รางวัลที่ 1", color: "bg-rose-50 text-rose-700 border-rose-200", sequence: 1 },
  nearBy: { label: "ข้างเคียงรางวัลที่ 1", color: "bg-orange-50 text-orange-700 border-orange-200", sequence: 2 },
  second: { label: "รางวัลที่ 2", color: "bg-amber-50 text-amber-700 border-amber-200", sequence: 3 },
  third: { label: "รางวัลที่ 3", color: "bg-emerald-50 text-emerald-700 border-emerald-200", sequence: 4 },
  forth: { label: "รางวัลที่ 4", color: "bg-blue-50 text-blue border-blue/30", sequence: 5 },
  fith: { label: "รางวัลที่ 5", color: "bg-purple-50 text-purple-700 border-purple-200", sequence: 6 },
  firstThree: { label: "เลขหน้า 3 ตัว", color: "bg-indigo-50 text-indigo-700 border-indigo-200", sequence: 7 },
  lastThree: { label: "เลขท้าย 3 ตัว", color: "bg-cyan-50 text-cyan-700 border-cyan-200", sequence: 8 },
  lastTwo: { label: "เลขท้าย 2 ตัว", color: "bg-pink-50 text-pink-700 border-pink-200", sequence: 9 },
};

const getPrizeAmountTHB = (amount) => {
  if (typeof amount === "number") return amount;
  if (amount && typeof amount.THB === "number") return amount.THB;
  return Number(amount) || 0;
};

const UserPrizesTab = ({ userId, user }) => {
  const { configs, callApi } = Context();
  const [selectedRoundDate, setSelectedRoundDate] = useState(configs?.roundDate || "");
  const [loading, setLoading] = useState(true);
  const [userPrizeDocs, setUserPrizeDocs] = useState(null);

  const fetchUserPrizes = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const result = await callApi(
        "userPrizes-getAll",
        {
          roundDate: selectedRoundDate,
          queries: [["userId", userId]],
          limit: 100,
        },
        { notShowLoading: true }
      );
      setUserPrizeDocs(result || []);
    } catch (err) {
      console.warn("Failed to fetch user prizes:", err);
      setUserPrizeDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRoundDate) {
      fetchUserPrizes();
    }
  }, [userId, selectedRoundDate]);

  // Aggregate all individual prize items across documents
  const allPrizes = useMemo(() => {
    if (!userPrizeDocs || userPrizeDocs.length === 0) return [];
    return userPrizeDocs.flatMap((doc) => {
      const prizes = Array.isArray(doc.prizes) ? doc.prizes : [];
      return prizes.map((prize, idx) => ({
        ...prize,
        uniqueKey: `${doc.id || doc.roundDate}-${idx}-${prize.lotteryId || prize.number}`,
        paid: doc.paid,
        roundDate: doc.roundDate,
        accName: doc.accName,
        accNo: doc.accNo,
        bankName: doc.bankName,
      }));
    }).sort((a, b) => {
      const seqA = PRIZE_TRANSLATIONS[a.prizeType]?.sequence ?? 99;
      const seqB = PRIZE_TRANSLATIONS[b.prizeType]?.sequence ?? 99;
      return seqA - seqB;
    });
  }, [userPrizeDocs]);

  // Total prize amount in THB
  const totalPrizeAmount = useMemo(() => {
    if (!userPrizeDocs || userPrizeDocs.length === 0) return 0;
    return userPrizeDocs.reduce((sum, doc) => {
      return sum + getPrizeAmountTHB(doc.totalPrizes);
    }, 0);
  }, [userPrizeDocs]);

  // Check payment status across docs
  const isPaidAll = useMemo(() => {
    if (!userPrizeDocs || userPrizeDocs.length === 0) return false;
    return userPrizeDocs.every((doc) => doc.paid);
  }, [userPrizeDocs]);

  // Bank Info from prize document or fallback to user profile
  const payoutBank = useMemo(() => {
    const docWithBank = (userPrizeDocs || []).find((d) => d.bankName || d.accNo);
    if (docWithBank) {
      return {
        bankName: docWithBank.bankName || "-",
        accNo: docWithBank.accNo || "-",
        accName: docWithBank.accName || "-",
      };
    }
    return {
      bankName: user?.bank?.name || user?.bankName || "-",
      accNo: user?.bank?.accNo || user?.accNo || "-",
      accName: user?.bank?.accName || user?.accName || "-",
    };
  }, [userPrizeDocs, user]);

  return (
    <div className="space-y-4">
      <CardSection
        title="ประวัติการถูกรางวัล"
        icon={<Trophy className="w-4 h-4" />}
        action={
          <div className="flex items-center gap-2">
            {configs?.roundDates && (
              <select
                className="h-8 px-2.5 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-md shadow-2xs focus:outline-none focus:border-blue cursor-pointer"
                value={selectedRoundDate}
                onChange={(e) => setSelectedRoundDate(e.target.value)}
              >
                {configs.roundDates.map((rd) => (
                  <option value={rd} key={rd}>
                    งวด {rd}
                  </option>
                ))}
              </select>
            )}

            <button
              type="button"
              title="รีเฟรชข้อมูลรางวัล"
              onClick={fetchUserPrizes}
              disabled={loading}
              className="h-8 w-8 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md border border-slate-300 shadow-2xs flex items-center justify-center transition-all cursor-pointer disabled:opacity-50 shrink-0"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-navy" : ""}`} />
            </button>
          </div>
        }
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
            <Spinner className="w-8 h-8" />
            <p className="text-sm font-semibold text-slate-600">กำลังโหลดประวัติการถูกรางวัล...</p>
          </div>
        ) : allPrizes.length > 0 ? (
          <div className="space-y-4">
            {/* Overview Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Card 1: Total Won Amount */}
              <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-md p-3.5 flex flex-col justify-between gap-1 shadow-2xs">
                <span className="text-xs font-semibold text-emerald-800">ยอดเงินรางวัลรวมงวดนี้</span>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-extrabold font-mono text-emerald-600">
                    {totalPrizeAmount.toLocaleString()}
                  </span>
                  <span className="text-xs font-bold text-emerald-700">บาท</span>
                </div>
              </div>

              {/* Card 2: Total Winning Tickets */}
              <div className="bg-blue-50/60 border border-blue-200/80 rounded-md p-3.5 flex flex-col justify-between gap-1 shadow-2xs">
                <span className="text-xs font-semibold text-blue-800">จำนวนรางวัลที่ถูก</span>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-extrabold font-mono text-blue">
                    {allPrizes.length.toLocaleString()}
                  </span>
                  <span className="text-xs font-bold text-blue-700">รางวัล</span>
                </div>
              </div>

              {/* Card 3: Payout Status */}
              <div
                className={`border rounded-md p-3.5 flex flex-col justify-between gap-1 shadow-2xs ${isPaidAll
                    ? "bg-emerald-50/60 border-emerald-200/80 text-emerald-800"
                    : "bg-amber-50/60 border-amber-200/80 text-amber-800"
                  }`}
              >
                <span className="text-xs font-semibold">สถานะการโอนรางวัล</span>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md border ${isPaidAll
                        ? "bg-emerald-100/80 text-emerald-700 border-emerald-300"
                        : "bg-amber-100/80 text-amber-700 border-amber-300"
                      }`}
                  >
                    {isPaidAll ? (
                      <>
                        <Check className="w-3 h-3" />
                        <span>โอนเงินรางวัลเรียบร้อยแล้ว</span>
                      </>
                    ) : (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                        <span>รอโอนเงินรางวัล</span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Payout Bank Info Strip */}
            {(payoutBank.accNo !== "-" || payoutBank.bankName !== "-") && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-slate-400" />
                  <span className="font-semibold text-slate-700">บัญชีรับเงินรางวัล:</span>
                  <span>{payoutBank.bankName}</span>
                  <span className="font-mono font-bold text-slate-800">{payoutBank.accNo}</span>
                  {payoutBank.accName !== "-" && (
                    <span className="text-slate-500">({payoutBank.accName})</span>
                  )}
                </div>
              </div>
            )}

            {/* Winning Tickets Table */}
            <div className="border border-slate-200 rounded-md overflow-hidden shadow-2xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-100/80 border-b border-slate-200 text-slate-700 font-bold">
                      <th className="py-2.5 px-3">ลำดับ</th>
                      <th className="py-2.5 px-3">รางวัล</th>
                      <th className="py-2.5 px-3">หมายเลขสลาก</th>
                      <th className="py-2.5 px-3">เลขที่ถูกรางวัล</th>
                      <th className="py-2.5 px-3 text-right">เงินรางวัล</th>
                      <th className="py-2.5 px-3 text-center">สถานะ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {allPrizes.map((prize, index) => {
                      const prizeConf = PRIZE_TRANSLATIONS[prize.prizeType] || {
                        label: prize.prizeType || "รางวัล",
                        color: "bg-slate-100 text-slate-700 border-slate-200",
                      };
                      const prizeAmt = getPrizeAmountTHB(prize.prizeAmount);

                      return (
                        <tr key={prize.uniqueKey || index} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-2.5 px-3 text-slate-400 font-medium">
                            {index + 1}
                          </td>
                          <td className="py-2.5 px-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold border ${prizeConf.color}`}
                            >
                              {prizeConf.label}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 font-mono font-bold text-slate-900 tracking-wider text-sm">
                            {prize.number || "-"}
                          </td>
                          <td className="py-2.5 px-3 font-mono font-extrabold text-blue tracking-wider text-sm">
                            {prize.winner || "-"}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-600 text-sm">
                            {prizeAmt.toLocaleString()} ฿
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <span
                              className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-md border ${prize.paid
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200"
                                }`}
                            >
                              {prize.paid ? "จ่ายแล้ว" : "รอจ่าย"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-slate-400">
            <Trophy className="w-10 h-10 text-slate-300" />
            <p className="text-sm font-bold text-slate-700">ไม่พบประวัติการถูกรางวัลในงวดนี้</p>
            <p className="text-xs font-semibold text-slate-500">
              ลูกค้ารายนี้ไม่มีสลากที่ถูกรางวัลในงวดวันที่ {selectedRoundDate}
            </p>
          </div>
        )}
      </CardSection>
    </div>
  );
};

// ── Side Panel Component 1: Order History ─────────────────────────────────────

const OrderHistorySideCard = ({ userId }) => {
  const { configs, callApi } = Context();
  const [roundDate, setRoundDate] = useState(configs?.roundDate || "");
  const [orders, setOrders] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const fetchUserOrders = async () => {
    if (!userId) return;
    setLoadingOrders(true);
    try {
      const result = await callApi(
        "orders-getAll",
        {
          queries: [
            ["userId", userId],
            ["roundDate", roundDate],
          ],
          roundDate: roundDate,
          limit: 100,
          orderBy: ["createdAt", "desc"],
        },
        { notShowLoading: true }
      );
      setOrders(result || []);
    } catch (err) {
      console.warn("Failed to get orders:", err);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    if (roundDate) {
      fetchUserOrders();
    }
  }, [roundDate, userId]);

  return (
    <CardSection
      title="คำสั่งซื้อสลาก"
      icon={<Package className="w-4 h-4" />}
      action={
        <div className="flex items-center gap-1.5">
          {configs?.roundDates && (
            <select
              className="h-7 px-2 text-[11px] font-bold text-slate-700 bg-white border border-slate-300 rounded-md shadow-2xs focus:outline-none focus:border-blue cursor-pointer"
              value={roundDate}
              onChange={(e) => setRoundDate(e.target.value)}
            >
              {configs.roundDates.map((rd) => (
                <option value={rd} key={rd}>
                  งวด {rd}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            title="สร้างคำสั่งซื้อสลากใหม่"
            onClick={() => window.open(`/orders/create/${userId}`, "_blank")}
            className="h-7 w-7 bg-blue hover:bg-blue/90 text-white rounded-md shadow-xs flex items-center justify-center transition-all cursor-pointer shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      }
    >
      <div className="max-h-[360px] overflow-y-auto pr-1 space-y-2">
        {loadingOrders ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-slate-400">
            <Spinner className="w-6 h-6" />
            <p className="text-xs font-semibold">กำลังโหลดคำสั่งซื้อ...</p>
          </div>
        ) : orders && orders.length > 0 ? (
          orders.map((order) => {
            const isApproved = order.status === statuses.APPROVE;
            const isCancel = order.status === statuses.CANCEL;
            const isPaid = order.status === statuses.PAID;

            const badgeBg = isApproved
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : isCancel
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : isPaid
                  ? "bg-teal-50 text-teal-700 border-teal-200"
                  : "bg-amber-50 text-amber-700 border-amber-200";

            return (
              <div
                key={order.shortId || order.id}
                onClick={() => window.open(`/orders/${order.id}`, "_blank")}
                className="p-2.5 bg-slate-50/70 hover:bg-white border border-slate-200/80 rounded-md shadow-2xs hover:shadow-xs hover:border-blue/30 transition-all cursor-pointer flex flex-col gap-1.5 group"
              >
                <div className="flex items-center justify-between gap-1.5">
                  <div className="flex items-center gap-1 font-mono font-bold text-xs text-slate-800 group-hover:text-blue transition-colors">
                    <span>#{order.shortId || order.id}</span>
                    <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-blue opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md border ${badgeBg}`}>
                    {constant.statusesTranslate[order.status] || order.status}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-600 pt-1 border-t border-slate-200/60">
                  <span className="text-[11px] text-slate-500">{order.size} ใบ</span>
                  <span className="font-mono font-bold text-emerald-600">
                    {Number(order.totalPrice || 0).toLocaleString()} ฿
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center py-8 gap-1.5 text-slate-400">
            <Package className="w-6 h-6 text-slate-300" />
            <p className="text-xs font-semibold text-slate-600">ไม่พบคำสั่งซื้อในงวดนี้</p>
          </div>
        )}
      </div>
    </CardSection>
  );
};

// ── Side Panel Component 2: Admin Notes Timeline ──────────────────────────────

const AdminNotesSideCard = ({ user, userId, onRefreshUser }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [newNote, setNewNote] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast({
        className: "bg-rose-600 text-white font-semibold",
        title: "กรุณาระบุข้อความโน้ต",
      });
      return;
    }

    setNoteLoading(true);
    try {
      const { status } = await callApi(
        "users-updateNotes",
        {
          userId: userId,
          note: newNote.trim(),
        },
        { notShowLoading: true }
      );

      if (status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold",
          title: "เพิ่มบันทึกโน้ตสำเร็จ",
        });
        setNewNote("");
        if (onRefreshUser) onRefreshUser();
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold",
          title: "เพิ่มบันทึกโน้ตไม่สำเร็จ",
        });
      }
    } catch {
      toast({
        className: "bg-rose-600 text-white font-semibold",
        title: "เกิดข้อผิดพลาดในการบันทึกโน้ต",
      });
    } finally {
      setNoteLoading(false);
    }
  };

  const sortedNotes = useMemo(() => {
    return (user?.notes || []).slice().sort((a, b) => (b?.time || 0) - (a?.time || 0));
  }, [user?.notes]);

  return (
    <CardSection
      title={`บันทึกโน้ตติดตาม (${sortedNotes.length})`}
      icon={<FileText className="w-4 h-4" />}
    >
      {/* Note Input */}
      <div className="flex flex-col gap-2 pb-3 border-b border-slate-100">
        <textarea
          rows={2}
          className="w-full py-1.5 px-2.5 font-medium rounded-md text-xs text-slate-800 border border-slate-200 bg-white focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue/20 placeholder:text-slate-400 resize-none transition-all"
          placeholder="พิมพ์บันทึกข้อความ / ประวัติการติดต่อ..."
          value={newNote}
          disabled={noteLoading}
          onChange={(e) => setNewNote(e.target.value)}
        />
        <button
          type="button"
          disabled={noteLoading || !newNote.trim()}
          onClick={handleAddNote}
          className="w-full h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-md shadow-xs transition-all disabled:opacity-50 cursor-pointer flex items-center justify-center gap-1.5"
        >
          {noteLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          <span>เพิ่มบันทึก</span>
        </button>
      </div>

      {/* Note Timeline */}
      <div className="max-h-[320px] overflow-y-auto pr-1 pt-3 space-y-2.5">
        {sortedNotes.length > 0 ? (
          sortedNotes.map((noteItem, idx) => (
            <div
              key={idx}
              className="p-2.5 bg-slate-50 border border-slate-200/80 rounded-md text-xs space-y-1"
            >
              <div className="flex items-center justify-between gap-1">
                <span className="font-bold text-[11px] text-blue truncate">
                  {noteItem.by || "แอดมิน"}
                </span>
                <span className="text-[10px] text-slate-400 font-medium shrink-0">
                  {new Date(noteItem.time).toLocaleDateString("th-TH", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })} น.
                </span>
              </div>
              <p className="text-slate-700 whitespace-pre-line leading-relaxed text-[11px]">
                {noteItem.note}
              </p>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-6 gap-1.5 text-slate-400">
            <FileText className="w-6 h-6 text-slate-300" />
            <p className="text-xs font-semibold text-slate-600">ยังไม่มีบันทึกโน้ต</p>
          </div>
        )}
      </div>
    </CardSection>
  );
};

// ── Main UserInfo Component ───────────────────────────────────────────────────

export const UserInfo = () => {
  const { toast } = useToast();
  const { userId } = useParams();
  const { callApi, navigate, profileState } = Context();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [activeTab, setActiveTab] = useState("general");

  const getUserData = async () => {
    setLoading(true);
    try {
      const { status, data } = await callApi(
        "users-getUserInfoAdmin",
        { userId: userId },
        { notShowLoading: true }
      );

      if (status && data) {
        setUser(data);
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold",
          title: "ไม่พบข้อมูลผู้ใช้งาน",
        });
        navigate("/users");
      }
    } catch (err) {
      console.warn("Failed to get user info:", err);
      toast({
        className: "bg-rose-600 text-white font-semibold",
        title: "เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้งาน",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserData();
  }, [userId]);

  // Bulk Edit Handler
  const handleUpdateUser = async () => {
    const updateEntries = Object.entries(userInfo);
    if (updateEntries.length === 0) return;
    if (!window.confirm(`ยืนยันการบันทึกข้อมูลที่แก้ไข (${updateEntries.length} รายการ) หรือไม่?`)) return;

    const fields = updateEntries.map(([key, value]) => ({ key, value }));

    setSaving(true);
    try {
      const result = await callApi(
        "users-edit",
        { id: userId, fields },
        { notShowLoading: true }
      );
      if (result?.status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold",
          title: "บันทึกข้อมูลผู้ใช้เรียบร้อยแล้ว",
        });
        setUserInfo({});
        getUserData();
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold",
          title: result?.message || "เกิดข้อผิดพลาดในการบันทึกข้อมูล",
        });
      }
    } catch {
      toast({
        className: "bg-rose-600 text-white font-semibold",
        title: "เกิดข้อผิดพลาดในการบันทึกข้อมูล",
      });
    } finally {
      setSaving(false);
    }
  };


  const handleDiscard = () => {
    if (Object.keys(userInfo).length === 0) return;
    if (window.confirm("ยกเลิกการแก้ไขทั้งหมดและคืนค่าเดิมใช่หรือไม่?")) {
      setUserInfo({});
    }
  };

  const handleRemoveItem = (key) => {
    setUserInfo((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const handleSwitchUpdate = async (key, value) => {
    if (window.confirm("ยืนยันการอัปเดตข้อมูล ใช่หรือไม่?")) {
      setSaving(true);
      try {
        const { status, message } = await callApi(
          key === "verifiedId" ? "users-verifyId" : "users-edit",
          { id: userId, key, value },
          { notShowLoading: true }
        );
        toast({
          className: status ? "bg-emerald-600 text-white font-semibold" : "bg-rose-600 text-white font-semibold",
          title: message || (status ? "อัปเดตข้อมูลสำเร็จ" : "อัปเดตข้อมูลไม่สำเร็จ"),
        });
        if (status) getUserData();
      } catch {
        toast({
          className: "bg-rose-600 text-white font-semibold",
          title: "เกิดข้อผิดพลาดในการอัปเดต",
        });
      } finally {
        setSaving(false);
      }
    }
  };

  const handleInputChange = (key, value) => {
    setUserInfo((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Reset Password Prompt
  const handleResetPassword = async () => {
    const newPassword = window.prompt("ระบุรหัสผ่านใหม่สำหรับผู้ใช้งานนี้ (ต้องมีความยาว 6-10 ตัวอักษร):");
    if (newPassword !== null) {
      if (newPassword.trim() === "") return window.alert("กรุณาระบุรหัสผ่าน");
      if (newPassword.length < 6 || newPassword.length > 10) {
        return window.alert("รหัสผ่านต้องมีความยาว 6 ถึง 10 ตัวอักษร");
      }
      setSaving(true);
      try {
        const result = await callApi("users-adminResetPassword", {
          userId: userId,
          newPassword: newPassword,
        });
        if (result?.status) {
          toast({
            className: "bg-emerald-600 text-white font-semibold",
            title: "ตั้งรหัสผ่านใหม่เรียบร้อยแล้ว",
          });
        } else {
          toast({
            className: "bg-rose-600 text-white font-semibold",
            title: result?.message || "เกิดข้อผิดพลาดในการตั้งรหัสผ่านใหม่",
          });
        }
      } catch {
        toast({
          className: "bg-rose-600 text-white font-semibold",
          title: "เกิดข้อผิดพลาดในการตั้งรหัสผ่านใหม่",
        });
      } finally {
        setSaving(false);
      }
    }
  };

  // Login As User
  const handleLoginAsUser = async () => {
    setSaving(true);
    try {
      const result = await callApi("users-addAdminToken", { userId: userId });
      if (result?.adminToken) {
        window.open(constant.getAdminOrderUrl(result.adminToken), "_blank");
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold",
          title: "ไม่สามารถสร้าง Admin Token ได้",
        });
      }
    } catch {
      toast({
        className: "bg-rose-600 text-white font-semibold",
        title: "เกิดข้อผิดพลาดในการเข้าสู่ระบบแทนลูกค้า",
      });
    } finally {
      setSaving(false);
    }
  };

  // Main Tabs on Left
  const tabs = [
    { id: "general", label: "ข้อมูลส่วนตัวและยืนยันตัวตน", icon: <User className="w-4 h-4" /> },
    { id: "payments", label: "บัญชีรับเงินและที่อยู่จัดส่ง", icon: <CreditCard className="w-4 h-4" /> },
    { id: "points", label: "คะแนนและธุรกรรม", icon: <Coins className="w-4 h-4" /> },
    { id: "prizes", label: "ประวัติการถูกรางวัล", icon: <Trophy className="w-4 h-4" /> },
  ];

  return (
    <div className="w-full max-w-[1600px] mx-auto flex flex-col gap-4 pb-6">
      {/* Header */}
      <Header
        header="Users"
        subHeader={`ข้อมูลและจัดการผู้ใช้ / ${userId}`}
        icon={<UsersIcon className="text-blue w-6 h-6" />}
      />

      {loading ? (
        <div className="bg-white rounded-md p-16 shadow-xs border border-slate-200/80 flex flex-col items-center justify-center gap-3">
          <Spinner className="w-8 h-8" />
          <p className="text-sm font-semibold text-slate-500">กำลังโหลดข้อมูลผู้ใช้งาน...</p>
        </div>
      ) : (
        <>
          {/* Top Hero Profile Banner */}
          <HeroProfileBanner
            user={user}
            userId={userId}
            loading={saving}
            onResetPassword={handleResetPassword}
            onLoginAsUser={handleLoginAsUser}
          />

          {/* Main 2-Column Layout (Left: Form Tabs, Right: Orders & Notes Side Panel) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
            {/* Left: Main Profile & Setting Tabs (2 Columns on Large Screens) */}
            <div className="lg:col-span-2 space-y-4">
              {/* Tab Navigation */}
              <div className="flex items-center gap-1.5 overflow-x-auto bg-slate-100/80 p-1 rounded-md border border-slate-200/80 shadow-2xs">
                {tabs.map(({ id, label, icon }) => {
                  const isActive = activeTab === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveTab(id)}
                      className={`flex items-center gap-2 px-3.5 py-2 text-xs font-bold rounded-md transition-all whitespace-nowrap cursor-pointer ${isActive
                          ? "bg-white text-blue shadow-xs border border-slate-200/80"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                        }`}
                    >
                      {icon}
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active Tab Content */}
              <div className="min-h-[400px]">
                {activeTab === "general" && (
                  <GeneralInfoTab
                    user={user}
                    userInfo={userInfo}
                    loading={saving}
                    handleInputChange={handleInputChange}
                    handleSwitchUpdate={handleSwitchUpdate}
                    profileState={profileState}
                  />
                )}

                {activeTab === "payments" && (
                  <PaymentsShippingTab
                    user={user}
                    userInfo={userInfo}
                    loading={saving}
                    handleInputChange={handleInputChange}
                  />
                )}

                {activeTab === "points" && (
                  <PointsTransactionsTab
                    user={user}
                    userId={userId}
                    onRefreshUser={getUserData}
                  />
                )}

                {activeTab === "prizes" && (
                  <UserPrizesTab
                    userId={userId}
                    user={user}
                  />
                )}
              </div>
            </div>

            {/* Right: Side Panel with Orders & Notes (1 Column on Large Screens) */}
            <div className="lg:col-span-1 space-y-4">
              {/* Save controls stay in the layout, beside the form on desktop. */}
              <UserEditActions
                userInfo={userInfo}
                user={user}
                onSave={handleUpdateUser}
                onDiscard={handleDiscard}
                onRemoveItem={handleRemoveItem}
                loading={saving}
              />
              {/* 1. Orders Card */}
              <OrderHistorySideCard userId={userId} />

              {/* 2. Admin Notes Card */}
              <AdminNotesSideCard
                user={user}
                userId={userId}
                onRefreshUser={getUserData}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserInfo;
